// Overlay del onboarding guiado: velo oscuro con "spotlight" (recorte) sobre el
// elemento resaltado y un tooltip con el mensaje, la barra de progreso y los
// controles (anterior / siguiente / saltar / terminar).
//
// Se renderiza con `createPortal` a `document.body` para quedar POR ENCIMA de
// todo (tab-bar z-40, overlays z-50): usamos z-[60]. El recorte del velo se hace
// con cuatro paneles oscuros alrededor del rectángulo del objetivo (en vez de un
// box-shadow gigante), lo que permite animar y que el elemento resaltado siga
// siendo visible y, si se desea, clicable. El tooltip se posiciona según el
// `placement` del paso, con ajuste para no salirse de la pantalla.
//
// Si el objetivo de un paso no existe en el DOM (p. ej. una pestaña oculta por
// permisos que no se filtró, o un cambio de ruta en curso), el paso se muestra
// centrado como un mensaje normal (degradación elegante, nunca pantalla rota).

import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../ui/components.js';
import { useOnboarding } from './OnboardingContext.js';
import type { OnboardingStep } from './steps.js';

/** Rectángulo del elemento resaltado (coordenadas de viewport). */
interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Margen (px) del recorte alrededor del elemento resaltado. */
const PADDING = 8;
/** Separación (px) entre el elemento y el tooltip. */
const GAP = 14;
/** Ancho máximo del tooltip (px); el motor lo acota al viewport. */
const TOOLTIP_WIDTH = 320;

export function OnboardingTour() {
  const { active, current, index, steps, next, prev, skip, finish } = useOnboarding();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState<Rect | null>(null);
  // `null` = aún resolviendo el objetivo; evita un parpadeo del tooltip centrado.
  const [resolving, setResolving] = useState(false);

  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  // 1) Navegación: si el paso declara una ruta distinta a la actual, navegamos
  //    antes de buscar el objetivo. El efecto de abajo reintenta la medición.
  useEffect(() => {
    if (!active || !current) return;
    if (current.route && current.route !== location.pathname) {
      navigate(current.route);
    }
  }, [active, current, location.pathname, navigate]);

  // 2) Resolución y medición del objetivo. Reintenta brevemente para dar tiempo
  //    a que la vista destino monte el elemento tras una navegación.
  useLayoutEffect(() => {
    if (!active || !current) {
      setRect(null);
      return;
    }
    // Pasos centrados o sin objetivo: no hay recorte.
    if (!current.target || current.placement === 'center') {
      setRect(null);
      setResolving(false);
      return;
    }
    // Si el paso exige estar en una ruta y aún no llegamos, espera a la nav.
    if (current.route && current.route !== location.pathname) {
      setResolving(true);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    setResolving(true);

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        setResolving(false);
        return;
      }
      // Reintenta hasta ~1s (20 * 50ms) antes de rendirse → centrado.
      if (attempts < 20) {
        attempts += 1;
        window.setTimeout(measure, 50);
      } else {
        setRect(null);
        setResolving(false);
      }
    };
    measure();

    return () => {
      cancelled = true;
    };
  }, [active, current, index, location.pathname]);

  // 3) Re-medir en scroll/resize mientras hay un objetivo (la tab-bar es fija,
  //    pero el contenido puede desplazarse).
  useEffect(() => {
    if (!active || !current?.target) return;
    const remeasure = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [active, current]);

  // 4) Teclado: Escape salta, flechas navegan (accesibilidad).
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, skip, next, prev]);

  if (!active || !current) return null;

  const centered = !rect || current.placement === 'center' || (!current.target && !resolving);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] select-none"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding.ariaLabel')}
    >
      {/* Velo: recorte alrededor del objetivo (4 paneles) o pantalla completa.
          En ambos casos usamos un DEGRADADO (no un plano opaco): más oscuro en
          la zona de foco y más translúcido hacia los bordes, para que el fondo
          de la app siga insinuándose y la tarjeta no quede flotando en el vacío. */}
      {rect && !centered ? (
        <Spotlight rect={rect} />
      ) : (
        <div
          className="absolute inset-0 backdrop-blur-sm transition-opacity duration-200"
          style={{
            // Degradado radial centrado: núcleo oscuro (foco de la tarjeta) que
            // se aclara hacia los bordes dejando ver el fondo de la app.
            background:
              'radial-gradient(130% 110% at 50% 45%, rgba(8,8,10,0.92) 0%, rgba(8,8,10,0.82) 42%, rgba(8,8,10,0.55) 100%)',
          }}
        />
      )}

      {/* Anillo resaltado alrededor del objetivo. */}
      {rect && !centered && (
        <div
          className="absolute rounded-2xl border-2 border-primary shadow-[0_0_0_4px_rgba(56,189,248,0.25)] pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }}
        />
      )}

      {/* Tooltip / tarjeta del mensaje. */}
      <TourCard
        step={current}
        rect={centered ? null : rect}
        index={index}
        total={total}
        isFirst={isFirst}
        isLast={isLast}
        onNext={next}
        onPrev={prev}
        onSkip={skip}
        onFinish={finish}
      />
    </div>,
    document.body,
  );
}

/**
 * Cuatro paneles que oscurecen todo menos el rectángulo del objetivo. En vez de
 * un negro plano, cada panel lleva un DEGRADADO que es más oscuro JUNTO al
 * recorte (para destacar el objetivo) y más translúcido hacia el borde de la
 * pantalla, de modo que el fondo de la app siga viéndose y las opciones no
 * queden "huérfanas en el aire".
 */
function Spotlight({ rect }: { rect: Rect }) {
  const top = Math.max(0, rect.top - PADDING);
  const left = Math.max(0, rect.left - PADDING);
  const right = rect.left + rect.width + PADDING;
  const bottom = rect.top + rect.height + PADDING;
  const panel = 'absolute backdrop-blur-sm transition-all duration-200';
  // Opacidades del degradado: borde junto al recorte (fuerte) → borde de pantalla (suave).
  const strong = 'rgba(8,8,10,0.9)';
  const soft = 'rgba(8,8,10,0.58)';
  // Cada panel se oscurece hacia el recorte: el gradiente apunta DESDE el borde
  // de pantalla (suave) HACIA el objetivo (fuerte).
  const grad = (dir: string) => `linear-gradient(${dir}, ${soft} 0%, ${strong} 100%)`;
  return (
    <>
      {/* Arriba: se oscurece hacia abajo (hacia el objetivo). */}
      <div
        className={panel}
        style={{ top: 0, left: 0, right: 0, height: top, background: grad('to bottom') }}
      />
      {/* Abajo: se oscurece hacia arriba. */}
      <div
        className={panel}
        style={{ top: bottom, left: 0, right: 0, bottom: 0, background: grad('to top') }}
      />
      {/* Izquierda: se oscurece hacia la derecha. */}
      <div
        className={panel}
        style={{ top, left: 0, width: left, height: bottom - top, background: grad('to right') }}
      />
      {/* Derecha: se oscurece hacia la izquierda. */}
      <div
        className={panel}
        style={{ top, left: right, right: 0, height: bottom - top, background: grad('to left') }}
      />
    </>
  );
}

/** Posición del tooltip según el `placement` del paso y el rect del objetivo. */
function computePosition(
  rect: Rect | null,
  placement: OnboardingStep['placement'],
): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(TOOLTIP_WIDTH, vw - 24);

  // Centrado (bienvenida/cierre o sin objetivo).
  if (!rect) {
    return {
      width,
      left: (vw - width) / 2,
      top: Math.max(24, vh / 2 - 140),
    };
  }

  const clampLeft = (x: number) => Math.min(Math.max(12, x), vw - width - 12);
  const centerX = clampLeft(rect.left + rect.width / 2 - width / 2);

  // Para la tab-bar inferior (placement top) el tooltip va ARRIBA del objetivo.
  const place =
    placement && placement !== 'auto' ? placement : rect.top > vh / 2 ? 'top' : 'bottom';

  switch (place) {
    case 'top':
      return { width, left: centerX, bottom: vh - rect.top + GAP };
    case 'bottom':
      return { width, left: centerX, top: rect.top + rect.height + GAP };
    case 'left':
      return { width, left: Math.max(12, rect.left - width - GAP), top: rect.top };
    case 'right':
      return { width, left: clampLeft(rect.left + rect.width + GAP), top: rect.top };
    default:
      return { width, left: centerX, top: rect.top + rect.height + GAP };
  }
}

interface TourCardProps {
  step: OnboardingStep;
  rect: Rect | null;
  index: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

function TourCard({
  step,
  rect,
  index,
  total,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onSkip,
  onFinish,
}: TourCardProps) {
  const { t } = useTranslation();
  const pos = computePosition(rect, step.placement);

  return (
    <div
      className="absolute rounded-2xl bg-surface-deck/98 border border-border-subtle shadow-2xl p-space-lg flex flex-col gap-space-sm pointer-events-auto"
      style={pos}
    >
      {/* Progreso: puntos + "paso X de N". */}
      <div className="flex items-center justify-between gap-space-sm">
        <div className="flex items-center gap-1.5" aria-hidden>
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-primary' : 'w-1.5 bg-on-surface-variant/40'
              }`}
            />
          ))}
        </div>
        <span className="font-badge-micro text-badge-micro uppercase tracking-widest text-on-surface-variant">
          {t('onboarding.progress', { current: index + 1, total })}
        </span>
      </div>

      {/* Título + icono. */}
      <h2 className="flex items-center gap-space-xs font-headline-sm text-headline-sm font-bold text-on-surface">
        {step.icon && <Icon name={step.icon} className="text-primary text-[22px]" />}
        {t(step.titleKey)}
      </h2>

      {/* Cuerpo del mensaje. */}
      <p className="font-body-sm text-body-sm text-on-surface-variant leading-snug">
        {t(step.bodyKey)}
      </p>

      {/* Controles. */}
      <div className="flex items-center justify-between gap-space-sm mt-space-xs">
        <button
          type="button"
          className="font-label-caps text-label-caps uppercase tracking-wider text-on-surface-variant hover:text-on-surface"
          onClick={onSkip}
        >
          {t('onboarding.skip')}
        </button>
        <div className="flex items-center gap-space-xs">
          {!isFirst && (
            <button type="button" className="btn-secondary btn-sm" onClick={onPrev}>
              <Icon name="arrow_back" className="text-[16px]" /> {t('onboarding.prev')}
            </button>
          )}
          {isLast ? (
            <button type="button" className="btn-primary btn-sm" onClick={onFinish}>
              <Icon name="check" className="text-[16px]" /> {t('onboarding.finish')}
            </button>
          ) : (
            <button type="button" className="btn-primary btn-sm" onClick={onNext}>
              {t('onboarding.next')} <Icon name="arrow_forward" className="text-[16px]" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
